/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { liftLoadedState, mapLoaded, mapLoadedUnpack } from "models/loaded";
import { LoadedContent } from "src/components/common/loading/loadedContent";
import { useTenantTranslation } from "hooks/formatters";

import { MixDetailsPropertiesTab } from "./MixDetailsPropertiesTab";
import { DrawerLayout } from "../layout";
import MixDetailsFormattedName from "./MixDetailsFormattedName";
import {
  useChemicalElements,
  useChemistryGroups,
  useSteelGrades,
} from "contexts/search/SearchContextProvider";

interface BaseProps {
  group: number;
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface PropertiesProps {
  group: number;
}

type Props = BaseProps & PropertiesProps;

export const MixPropertiesPanel = ({ group, open, setOpen }: Props) => {
  const { t } = useTenantTranslation();

  const chemistryGroup = mapLoadedUnpack(
    useChemistryGroups(),
    (groups) => groups.byId[group] ?? null
  );

  if (chemistryGroup === null) {
    /* Sometimes, the data grid's data model lags behind the actual data,
      causing IDs of chef groups to be passed to the render function when
      the renderer has already switched to looking up product groups.  This
      can momentarily cause the product group to not exist in the lookup,
      which can be solved by having this fail gracefully */
    return null;
  }

  return (
    <DrawerLayout
      title={t("mixProperties")}
      open={open}
      doClose={() => {
        setOpen(false);
      }}
      width={500}
    >
      <LoadedPropertiesTab group={group} />
    </DrawerLayout>
  );
};

const LoadedPropertiesTab = ({ group }: PropertiesProps) => {
  const chemistryGroups = useChemistryGroups();
  const chemicalElements = useChemicalElements();
  const steelGrades = useSteelGrades();

  const chemistryGroup = mapLoaded(
    chemistryGroups,
    (chemistryGroups) => chemistryGroups.byId[group]!
  );

  const dependencies = liftLoadedState({
    chemicalElements,
    chemistryGroup,
    steelGrades,
  });

  return (
    <LoadedContent data={dependencies}>
      {({
        chemistryGroup,
        chemicalElements: { byId: elements },
        steelGrades,
      }) => (
        <>
          <MixDetailsFormattedName
            chemistryGroupName={chemistryGroup.name}
            multipleMixes={false}
            mix={0}
          />
          <MixDetailsPropertiesTab
            chefGroupId={group}
            maximumFailureRate={chemistryGroup.max_failure_rate}
            defaultTargetNumberOfBaskets={
              chemistryGroup.default_target_num_baskets
            }
            chemicalConstraints={chemistryGroup.chemical_constraints}
            steelGrades={chemistryGroup.steel_grades.map(
              ({ id }) => steelGrades.byId[id]!
            )}
            elements={elements}
          />
        </>
      )}
    </LoadedContent>
  );
};
